// Purchase requests (M6): what someone asked to buy, from which supplier, and what the
// manager decided in the approvals inbox (D-060: one approval path). An approved request
// has made its purchase order.

import type { PurchaseRequest } from '@enp/domain/api';
import { PURCHASE_REQUEST_STATUSES } from '@enp/domain/purchasing';
import {
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  formatMoney,
  Money,
  PageHeader,
  type ColumnDef,
} from '@enp/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, FilePlus2, Lightbulb, Plus, SearchX, X } from 'lucide-react';
import { useMemo } from 'react';
import { requestMoney } from '../../../../purchasing/figures.js';
import {
  KeyHints,
  PurchasingViews,
  RequestStatusBadge,
  TableSkeleton,
  useCan,
  usePageKeys,
} from '../../../../purchasing/parts.js';
import {
  allSuppliersQuery,
  type RequestsSearch,
  requestsQuery,
  requestsSearchSchema,
} from '../../../../purchasing/queries.js';
import { REQUEST_ORIGIN, REQUEST_STATUS } from '../../../../purchasing/words.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { FilterSelect, LoadMoreFooter } from '../../../../stock/components.js';
import { clean } from '../../../../stock/queries.js';

export const Route = createFileRoute('/_app/purchasing/requests/')({
  validateSearch: requestsSearchSchema,
  component: RequestsPage,
});

const col = createColumnHelper<PurchaseRequest>();

/**
 * A request's money in the list (UI6A-1): once approved, what was ordered (the order's
 * value), with what was asked for under it; rejected, nothing ordered.
 */
function RequestValue({ r }: { r: PurchaseRequest }) {
  const m = requestMoney(r);
  const asked = m.asked === null ? 'no prices' : formatMoney(m.asked);
  if (m.kind === 'waiting') {
    return (
      <span className="flex flex-col items-end leading-tight" data-testid="request-value">
        {m.asked === null ? (
          <span className="text-sm text-muted">No prices</span>
        ) : (
          <Money cents={m.asked} className="font-semibold text-fg" />
        )}
        <span className="whitespace-nowrap text-xs text-muted">
          asked{m.askedUnpriced > 0 ? `, + ${m.askedUnpriced} unpriced` : ''}
        </span>
      </span>
    );
  }
  if (m.kind === 'nothing') {
    return (
      <span className="flex flex-col items-end leading-tight" data-testid="request-value">
        <span className="text-sm font-medium text-fg">Nothing ordered</span>
        <span className="whitespace-nowrap text-xs text-muted line-through">asked {asked}</span>
      </span>
    );
  }
  return (
    <span className="flex flex-col items-end leading-tight" data-testid="request-value">
      <Money
        cents={m.ordered}
        className={cn('font-semibold text-fg', m.cancelled && 'text-muted line-through')}
      />
      <span className="whitespace-nowrap text-xs text-muted">
        {m.cancelled ? 'order cancelled' : 'ordered'}
        {m.orderedUnpriced > 0 ? `, + ${m.orderedUnpriced} unpriced` : ''}
        {m.differs ? ` · asked ${asked}` : ''}
      </span>
    </span>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<PurchaseRequest, any>[] = [
  col.display({
    id: 'number',
    header: 'Request',
    meta: { width: 'min-w-40' },
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <Link
          to="/purchasing/requests/$requestId"
          params={{ requestId: row.original.id }}
          tabIndex={-1}
          className="whitespace-nowrap font-mono text-sm font-medium text-fg hover:underline"
        >
          {row.original.number}
        </Link>
        <span className="text-xs text-muted">{REQUEST_ORIGIN[row.original.origin]}</span>
      </span>
    ),
  }),
  col.display({
    id: 'supplier',
    header: 'Supplier',
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <span className="font-medium text-fg">{row.original.supplier.name}</span>
        <span className="text-xs text-muted">
          {row.original.lines.length} {row.original.lines.length === 1 ? 'product' : 'products'}
        </span>
      </span>
    ),
  }),
  col.display({
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span className="flex flex-col items-start gap-1">
        <RequestStatusBadge status={row.original.status} />
        {row.original.purchase_order && (
          <span className="whitespace-nowrap text-xs text-muted">
            → <span className="font-mono">{row.original.purchase_order.number}</span>
          </span>
        )}
      </span>
    ),
  }),
  col.display({
    id: 'requested',
    header: 'Asked',
    meta: { className: 'hidden lg:table-cell' },
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight text-sm">
        <AccountDate value={row.original.requested_at} withTime />
        <span className="text-muted">{row.original.requested_by?.name ?? 'Unknown'}</span>
      </span>
    ),
  }),
  col.display({
    id: 'value',
    header: 'Value ex VAT',
    meta: { align: 'right', width: 'w-44' },
    cell: ({ row }) => <RequestValue r={row.original} />,
  }),
  col.display({
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    meta: { width: 'w-8' },
    cell: () => <ChevronRight aria-hidden className="size-4 text-muted" />,
  }),
];

function RequestsPage() {
  useDocumentTitle('Purchase requests');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canRequest = useCan('purchasing.request');
  const suppliers = useQuery(allSuppliersQuery());
  const list = useInfiniteQuery(requestsQuery(search));
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const filtered = Boolean(search.status || search.supplier);
  const setSearch = (patch: Partial<RequestsSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  usePageKeys({
    n: canRequest ? () => void navigate({ to: '/purchasing/requests/new' }) : undefined,
  });

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No requests match"
      description="No purchase request fits these filters."
      action={
        <Button
          variant="secondary"
          icon={<X aria-hidden />}
          onClick={() => void navigate({ search: {} })}
        >
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={FilePlus2}
      title="No purchase requests yet"
      description="A request asks for stock from one supplier. It waits in the approvals inbox; a manager decides it product by product, and approving makes the purchase order."
      action={
        canRequest ? (
          <>
            <Button asChild>
              <Link to="/purchasing/restock">
                <Lightbulb aria-hidden />
                Start from the restock suggestion
              </Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/purchasing/requests/new">
                <Plus aria-hidden />
                New purchase request
              </Link>
            </Button>
          </>
        ) : undefined
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Purchasing"
        description="Requests wait in the approvals inbox; a manager decides each product, and approving makes the order."
        actions={
          canRequest ? (
            <Button asChild>
              <Link to="/purchasing/requests/new">
                <Plus aria-hidden />
                New purchase request
              </Link>
            </Button>
          ) : undefined
        }
      />
      <PurchasingViews current="requests" />
      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Request filters"
      >
        <FilterSelect
          label="Status"
          value={search.status}
          allLabel="All statuses"
          options={PURCHASE_REQUEST_STATUSES.map((s) => ({
            value: s,
            label: REQUEST_STATUS[s].label,
          }))}
          onChange={(v) => setSearch({ status: v as RequestsSearch['status'] })}
        />
        <FilterSelect
          label="Supplier"
          value={search.supplier}
          allLabel="All suppliers"
          options={(suppliers.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }))}
          onChange={(v) => setSearch({ supplier: v })}
        />
        {filtered && (
          <Button
            variant="ghost"
            icon={<X aria-hidden />}
            onClick={() => void navigate({ search: {} })}
          >
            Clear
          </Button>
        )}
        <span className="ml-auto">
          <KeyHints keys={canRequest ? [['n', 'new request']] : []} />
        </span>
      </div>
      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The requests"
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <TableSkeleton
          label="Purchase requests"
          columns={[
            { header: 'Request', className: 'min-w-40', twoLines: true },
            { header: 'Supplier', twoLines: true },
            { header: 'Status' },
            { header: 'Asked', className: 'hidden lg:table-cell', twoLines: true },
            { header: 'Value ex VAT', align: 'right', className: 'w-44', twoLines: true },
            { header: '', className: 'w-8', empty: true },
          ]}
        />
      ) : (
        <DataTable
          label="Purchase requests"
          columns={columns}
          data={items}
          loading={false}
          empty={empty}
          filterable={false}
          getRowId={(r) => r.id}
          onRowActivate={(r) =>
            void navigate({ to: '/purchasing/requests/$requestId', params: { requestId: r.id } })
          }
          footer={
            items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                noun={['request', 'requests']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-22rem)]"
        />
      )}
    </>
  );
}
